import { isGeoPointValid, normalizeGeoPoint } from '@@partner:helpers/util'
import { GeoPoint } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import { useLazyFetchShopsInGeoPolygon } from '@@partner:graphql/query/shop/shopsInGeoPolygon.gql'
import { useLazyFetchZonesInGeoPolygon } from '@@partner:graphql/query/deliveryZone/zonesInGeoPolygon.gql'

import L, {
  LatLngLiteral,
  Polygon as LeafletEditablePolygon,
  Map as LeafletMap
} from 'leaflet'
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer
} from 'react-leaflet'
import 'leaflet-editable'

import { ROUTE_SHOPS_EDIT_SHOP } from '@@partner:config/routes'

import 'leaflet/dist/leaflet.css'
import '../DeliveryZones.scss'
import MapMarker from '@components/common/MapMarker'

export const DEFAULT_MAP_CENTER: GeoPoint = {
  latitude: 50.326044,
  longitude: 40.480974
}

const DEFAULT_MAP_ZOOM = 10

const makePolygonFromZone = (zone: GeoPoint[]) =>
  L.polygon(convertGeoPointsZoneToLeafletZone(zone), { 
    stroke: true,
    color: '#e9833a'
  })

const convertGeoPointsZoneToLeafletZone = (zone: GeoPoint[]) =>
  zone.map((value) => convertGeoPointToLeafletLatLng(value))

export const convertGeoPointToLeafletLatLng = (point: GeoPoint): LatLngLiteral => ({
  lat: point.latitude as number,
  lng: point.longitude as number
})

const convertLeafletLatLngToGeoPoint = (
  latLngExpr: LatLngLiteral
): GeoPoint => normalizeGeoPoint({
  latitude: latLngExpr.lat,
  longitude: latLngExpr.lng
})

const makeMapCenter = (centerPoint: GeoPoint): LatLngLiteral =>
  convertGeoPointToLeafletLatLng(
    isGeoPointValid(centerPoint) ? centerPoint : DEFAULT_MAP_CENTER
  )

type Props = {
  zoneId: GQL_ID | null
  zoneGeoPoints: GeoPoint[]
  onZoneChanged: (value: GeoPoint[]) => void
  shopId?: GQL_ID | null
  fullscreen?: boolean
}

export default function SingleZoneMap ({
  zoneId,
  zoneGeoPoints,
  onZoneChanged,
  shopId,
  fullscreen = false
}: Props) {
  const { brandSlug } = useParams<any>()
  
  const [ mapCenter, setMapCenter ] = useState(() =>
    makeMapCenter(DEFAULT_MAP_CENTER)
  )
  const [ map, setMap ] = useState<LeafletMap>()
  const [ currentPolygonObj, setCurrentPolygonObj ] =
    useState<LeafletEditablePolygon | null>(null)
  const [ isEditing, setIsEditing ] = useState(false)
  const [ isDrawingStarted, setIsDrawingStarted ] = useState(false)
  const lastInitializedZoneIdRef = useRef<GQL_ID | null | undefined>(undefined)

  const [ fetchShops, { data: shopsData } ] = useLazyFetchShopsInGeoPolygon()
  const [ fetchZones, { data: zonesData } ] = useLazyFetchZonesInGeoPolygon()

  // Храним полигоны соседних зон
  const [ nearbyPolygons, setNearbyPolygons ] = useState<L.Polygon[]>([])

  const loadForBounds = useCallback(
    (map: L.Map) => {
      const bounds = map.getBounds()
      const zone = [
        convertLeafletLatLngToGeoPoint(bounds.getNorthWest()),
        convertLeafletLatLngToGeoPoint(bounds.getNorthEast()),
        convertLeafletLatLngToGeoPoint(bounds.getSouthEast()),
        convertLeafletLatLngToGeoPoint(bounds.getSouthWest())
      ]
      fetchShops({
        variables: {
          corners: zone
        }
      })
      fetchZones({
        variables: {
          corners: zone
        }
      })
    },
    [ fetchShops, fetchZones ]
  )

  useLayoutEffect(() => {
    if (map) {
      const handleDragEnd = () => loadForBounds(map)
      const handleZoomEnd = () => loadForBounds(map)

      map.on('dragend', handleDragEnd)
      map.on('zoomend', handleZoomEnd)

      return () => {
        map.off('dragend', handleDragEnd)
        map.off('zoomend', handleZoomEnd)
      }
    }
  }, [ map, loadForBounds ])

  const handlePolygonEditing = useCallback(
    (e: { layer: LeafletEditablePolygon }) => {
      const coords = e.layer.getLatLngs().flat(2)
      onZoneChanged?.(
        coords.map((latLngObj) => convertLeafletLatLngToGeoPoint(latLngObj))
      )
    },
    [ onZoneChanged ]
  )

  // Инициализация полигона при загрузке зоны
  useEffect(() => {
    if (!map) return

    const zoneChanged = lastInitializedZoneIdRef.current !== zoneId

    // Пока редактируется текущая зона, не пересоздаём полигон из пропсов.
    // Иначе добавление новых вершин откатывает изменения и дергает fitBounds.
    if (!zoneChanged && currentPolygonObj) {
      return
    }

    lastInitializedZoneIdRef.current = zoneId
    
    // Не сбрасываем если рисование было начато вручную
    if (isDrawingStarted) return

    // Удаляем предыдущий полигон
    if (currentPolygonObj) {
      currentPolygonObj.removeFrom(map)
      map.off('editable:editing', handlePolygonEditing)
    }

    if (zoneGeoPoints && zoneGeoPoints.length > 0) {
      // Существующая зона - отображаем и делаем редактируемой
      const polygon = makePolygonFromZone(zoneGeoPoints)
      polygon.addTo(map)
      polygon.enableEdit()
      map.on('editable:editing', handlePolygonEditing)
      setCurrentPolygonObj(polygon)
      setIsEditing(true)
      setIsDrawingStarted(false)

      // Центрируем карту на зоне
      const bounds = polygon.getBounds()
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [ 50, 50 ] })
      }
    }
    else {
      // Новая зона или зона без точек - ждем начала рисования
      setCurrentPolygonObj(null)
      setIsEditing(false)
    }
  }, [ map, zoneId, zoneGeoPoints, currentPolygonObj, handlePolygonEditing, isDrawingStarted ])

  // Функция начала рисования полигона
  const startDrawing = useCallback(() => {
    if (!map || isEditing) return
    
    setIsDrawingStarted(true)
    const newPolygon: L.Polygon = map.editTools.startPolygon()
    map.on('editable:editing', handlePolygonEditing)
    setCurrentPolygonObj(newPolygon)
    setIsEditing(true)
  }, [ map, isEditing, handlePolygonEditing ])

  // Функция очистки полигона
  const clearPolygon = useCallback(() => {
    if (!map || !currentPolygonObj) return
    
    currentPolygonObj.removeFrom(map)
    map.off('editable:editing', handlePolygonEditing)
    setCurrentPolygonObj(null)
    setIsEditing(false)
    setIsDrawingStarted(false)
    onZoneChanged([])
  }, [ map, currentPolygonObj, handlePolygonEditing, onZoneChanged ])

  // Отображение соседних зон (только для просмотра, без возможности клика)
  useEffect(() => {
    if (!map || !zonesData?.zonesInGeoPolygon) return

    // Удаляем старые полигоны соседних зон
    nearbyPolygons.forEach((polygon) => polygon.removeFrom(map))

    const newPolygons: L.Polygon[] = []

    zonesData.zonesInGeoPolygon.forEach((zone) => {
      // Пропускаем текущую редактируемую зону
      if (zone.id === zoneId) return

      if (zone.zoneGeoDeliveryZone && zone.zoneGeoDeliveryZone.length > 0) {
        const polygon = L.polygon(
          convertGeoPointsZoneToLeafletZone(zone.zoneGeoDeliveryZone),
          {
            color: '#888888',
            fillColor: '#cccccc',
            fillOpacity: 0.3,
            weight: 2,
            interactive: false // Отключаем интерактивность - нельзя кликать
          }
        )
        polygon.addTo(map)
        
        // Добавляем тултип с названием зоны
        polygon.bindTooltip(zone.name || `Зона #${zone.id}`, {
          permanent: false,
          direction: 'center',
          className: 'zone-tooltip'
        })
        
        newPolygons.push(polygon)
      }
    })

    setNearbyPolygons(newPolygons)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ map, zonesData?.zonesInGeoPolygon, zoneId ])

  // Координаты магазинов
  const positions = useMemo(() => {
    if (shopsData?.shopsInGeoPolygon && shopsData.shopsInGeoPolygon.length > 0) {
      return shopsData.shopsInGeoPolygon
        .map((shop) => {
          const selected = shopId === shop.id
          if (shop.id && shop?.shopGeoPoint) {
            return {
              id: shop.id,
              name: shop.name,
              address: shop.address,
              point: convertGeoPointToLeafletLatLng(shop.shopGeoPoint),
              selected
            }
          }
          return undefined
        })
        .filter((zone) => zone !== undefined)
    }
    return []
  }, [ shopsData?.shopsInGeoPolygon, shopId ])

  const whenMapCreated = (map: LeafletMap) => {
    setMap(map)
    loadForBounds(map)
    // Отключаем зум по двойному клику, чтобы можно было рисовать полигон
    map.doubleClickZoom.disable()
    // Также отключаем boxZoom чтобы избежать непредвиденных зумов
    map.boxZoom.disable()
  }

  const handleSetCenter = useCallback(
    (position: GeolocationPosition) => {
      const geoPoint = convertGeoPointToLeafletLatLng(position.coords)

      if (map && zoneGeoPoints.length === 0) {
        map.setView(geoPoint, 12)
        loadForBounds(map)
      }
      setMapCenter(geoPoint)
    },
    [ map, loadForBounds, zoneGeoPoints.length ]
  )

  useEffect(() => {
    if (navigator.geolocation && zoneGeoPoints.length === 0) {
      navigator.geolocation.getCurrentPosition(handleSetCenter)
    }
  }, [ handleSetCenter, zoneGeoPoints.length ])

  return (
    <div style={{ position: 'relative' }}>
      {/* Панель управления рисованием */}
      <div 
        style={{ 
          position: 'absolute', 
          top: 10, 
          left: 10, 
          zIndex: 1000,
          display: 'flex',
          gap: 8
        }}
      >
        {!isEditing && (
          <button
            onClick={startDrawing}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            Начать рисование зоны
          </button>
        )}
        {isEditing && currentPolygonObj && (
          <button
            onClick={clearPolygon}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            Очистить зону
          </button>
        )}
      </div>

      <MapContainer
        center={mapCenter}
        zoom={DEFAULT_MAP_ZOOM}
        scrollWheelZoom={true}
        editable={true}
        whenCreated={whenMapCreated}
        style={{
          zIndex: 30,
          height: fullscreen ? '100vh' : '400px',
          width: '100%',
          borderRadius: fullscreen ? 0 : '8px'
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Маркеры магазинов */}
        {positions &&
          positions.length > 0 &&
          positions.map((position) => (
            <Marker
              icon={MapMarker(position.selected ? 'red' : 'blue')}
              key={`marker:${position.id}`}
              position={position.point}
            >
              <Popup>
                <div>{position.id}</div>
                <div>{position.name} {position.address}</div>
                <Link
                  to={generatePath(ROUTE_SHOPS_EDIT_SHOP, {
                    brandSlug,
                    shopId: position.id
                  })}
                  target="_blank"
                >
                  Открыть
                </Link>
              </Popup>
            </Marker>
          ))}
      </MapContainer>
    </div>
  )
}
